import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { DonationCampaign, DonationRecord, PersatuanPaymentSettings, GelanggangDetail } from '../../types';
import {
  getDonationCampaignsDB,
  getDonationsDB,
  recordDonationDB,
  confirmDonationSuccessDB,
  markDonationFailedDB,
  getPersatuanPaymentSettingsDB,
  DEFAULT_PERSATUAN_PAYMENT_SETTINGS,
  getGelanggangDB
} from '../../services/db';
import { processUploadedQrToSvg } from '../../utils/qrProcessor';

import { createToyyibPayBill, verifyToyyibPayBillStatus } from '../../lib/toyyibpay';
import { useToast } from '../../components/common/Toast';
import { PageHeader } from '../../components/common/PageHeader';
import { OfficialDonationReceiptModal } from '../../components/donations/OfficialDonationReceiptModal';
import { isSuperAdmin, hasRolePermission } from '../../utils/formatters';
import {
  Heart,
  Users,
  Calendar,
  Share2,
  Sparkles,
  ArrowLeft,
  Edit3,
  CheckCircle2,
  Copy,
  Check,
  CreditCard,
  QrCode,
  Upload,
  Clock,
  RefreshCw,
  ExternalLink,
  ChevronRight,
  ChevronLeft,
  EyeOff,
  UserCheck,
  MessageSquare,
  FileText,
  ShieldCheck,
  Loader2,
  Download
} from 'lucide-react';
import { formatCurrencyMYR } from '../../utils/formatters';

interface CampaignDetailPageProps {
  campaignId: string;
  onBack: () => void;
  onEdit?: (campaign: DonationCampaign) => void;
}

const PRESET_AMOUNTS = [10, 30, 50, 100, 250, 500];

const getCategoryColor = (cat: DonationCampaign['category']) => {
  switch (cat) {
    case 'Pembangunan Gelanggang':
      return 'bg-amber-50 text-amber-800 border-amber-200';
    case 'Kebajikan & Asnaf':
      return 'bg-red-50 text-red-700 border-red-200';
    case 'Peralatan & Busana':
      return 'bg-slate-100 text-slate-800 border-slate-200';
    case 'Kontinjen & Sukan':
      return 'bg-blue-50 text-blue-800 border-blue-200';
    default:
      return 'bg-slate-50 text-slate-700 border-slate-200';
  }
};

export const CampaignDetailPage: React.FC<CampaignDetailPageProps> = ({
  campaignId,
  onBack,
  onEdit
}) => {
  const { currentUser } = useAuth();
  const { showToast } = useToast();

  const [campaign, setCampaign] = useState<DonationCampaign | null>(null);
  const [donations, setDonations] = useState<DonationRecord[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // In-Page Donation Form State
  const [step, setStep] = useState<number>(1);
  const [amount, setAmount] = useState<number>(50);
  const [customAmountStr, setCustomAmountStr] = useState<string>('50');

  const [donorName, setDonorName] = useState<string>(currentUser?.name || '');
  const [isAnonymous, setIsAnonymous] = useState<boolean>(false);
  const [donorEmail, setDonorEmail] = useState<string>(currentUser?.email || '');
  const [donorPhone, setDonorPhone] = useState<string>(currentUser?.phone || '');
  const [message, setMessage] = useState<string>('');

  useEffect(() => {
    if (currentUser) {
      if (currentUser.name && !donorName) setDonorName(currentUser.name);
      if (currentUser.email && !donorEmail) setDonorEmail(currentUser.email);
      if (currentUser.phone && !donorPhone) setDonorPhone(currentUser.phone);
    }
  }, [currentUser]);


  const [paymentMethod, setPaymentMethod] = useState<'ToyyibPay FPX' | 'DuitNow QR' | 'Bank Transfer'>('DuitNow QR');
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [copiedAcc, setCopiedAcc] = useState<boolean>(false);
  const [transferRef, setTransferRef] = useState<string>('');

  const [pendingFpxBill, setPendingFpxBill] = useState<{ billCode: string; paymentUrl: string; donationId: string } | null>(null);
  const [isVerifyingFpx, setIsVerifyingFpx] = useState<boolean>(false);
  const [completedDonation, setCompletedDonation] = useState<DonationRecord | null>(null);
  const [isReceiptOpen, setIsReceiptOpen] = useState<boolean>(false);

  // Persatuan & Gelanggang Payment Configuration
  const [persatuanSettings, setPersatuanSettings] = useState<PersatuanPaymentSettings>(DEFAULT_PERSATUAN_PAYMENT_SETTINGS);
  const [gelanggangList, setGelanggangList] = useState<GelanggangDetail[]>([]);
  const [digitalSvgString, setDigitalSvgString] = useState<string>('');
  const [isProcessingSvg, setIsProcessingSvg] = useState<boolean>(false);

  const pollTimerRef = useRef<any>(null);

  const isManagement = Boolean(
    currentUser &&
    hasRolePermission(currentUser, ['Pengerusi', 'Timbalan Pengerusi', 'Setiausaha', 'Bendahari'])
  );

  const loadCampaignData = async () => {
    try {
      const allCampaigns = await getDonationCampaignsDB();
      const found = allCampaigns.find((c) => c.id === campaignId)
        || allCampaigns.find((c) => c.id.startsWith(campaignId) || campaignId.startsWith(c.id))
        || allCampaigns[0];
      if (found) {
        setCampaign(found);
      }
      const allDonations = await getDonationsDB(campaignId);
      setDonations(allDonations.filter((d) => d.status === 'Berjaya'));
    } catch {
    } finally {
      setIsLoading(false);
    }
  };

  const loadPaymentConfigs = () => {
    getPersatuanPaymentSettingsDB().then(setPersatuanSettings).catch(() => {});
    getGelanggangDB().then(setGelanggangList).catch(() => {});
  };

  useEffect(() => {
    loadCampaignData();
    loadPaymentConfigs();
    const handleUpdate = () => {
      loadCampaignData();
      loadPaymentConfigs();
    };
    const handleSettingsUpdated = (e: any) => {
      if (e?.detail) setPersatuanSettings(e.detail);
      loadPaymentConfigs();
    };
    window.addEventListener('asmm_db_updated', handleUpdate);
    window.addEventListener('asmm_donation_received', handleUpdate);
    window.addEventListener('asmm_payment_settings_updated', handleSettingsUpdated);
    return () => {
      window.removeEventListener('asmm_db_updated', handleUpdate);
      window.removeEventListener('asmm_donation_received', handleUpdate);
      window.removeEventListener('asmm_payment_settings_updated', handleSettingsUpdated);
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    };
  }, [campaignId]);

  // Enforce payment method compliance with /settings/payment (e.g. if FPX is disabled)
  useEffect(() => {
    if (!persatuanSettings.fpxEnabled && paymentMethod === 'ToyyibPay FPX') {
      setPaymentMethod(persatuanSettings.qrEnabled !== false ? 'DuitNow QR' : 'Bank Transfer');
    }
  }, [persatuanSettings.fpxEnabled, persatuanSettings.qrEnabled, paymentMethod]);

  // Determine Effective Bank & QR Account Destination
  const targetGelanggang = gelanggangList.find(g => g.name === campaign?.beneficiaryGelanggang);
  const isGelanggangCustom = targetGelanggang?.paymentPreference === 'qr_ketua' && Boolean(targetGelanggang?.qrAccountNo);

  const effectiveBankName = isGelanggangCustom
    ? (targetGelanggang?.qrBankName || 'Maybank')
    : (persatuanSettings.bankName || 'Bank Islam Malaysia Berhad');

  const effectiveAccountNo = isGelanggangCustom
    ? (targetGelanggang?.qrAccountNo || '-')
    : (persatuanSettings.accountNo || '02011010087654');

  const effectiveAccountHolder = isGelanggangCustom
    ? (targetGelanggang?.qrAccountName || targetGelanggang?.leaderName || 'Akaun Gelanggang')
    : (persatuanSettings.accountHolder || 'PERSATUAN SENI SILAT AWANG SULUNG MERAH MUDA NEGERI KEDAH');

  const effectiveQrImage = (isGelanggangCustom && targetGelanggang?.qrImageUrl)
    ? targetGelanggang.qrImageUrl
    : (persatuanSettings.qrImageUrl || '/badges/qr-persatuan-asmm.png');

  // Digital 1:1 SVG QR Auto-Processor
  useEffect(() => {
    if (!effectiveQrImage) {
      setIsProcessingSvg(false);
      setDigitalSvgString('');
      return;
    }

    const isAlreadySvg = effectiveQrImage.startsWith('data:image/svg+xml') || effectiveQrImage.trim().startsWith('<svg');
    if (isAlreadySvg) {
      setIsProcessingSvg(false);
      if (effectiveQrImage.trim().startsWith('<svg')) {
        setDigitalSvgString(effectiveQrImage);
      } else if (effectiveQrImage.startsWith('data:image/svg+xml')) {
        try {
          const decoded = decodeURIComponent(effectiveQrImage.replace('data:image/svg+xml;charset=utf-8,', '').replace('data:image/svg+xml,', ''));
          setDigitalSvgString(decoded);
        } catch { }
      }
      return;
    }

    let isMounted = true;
    setIsProcessingSvg(true);
    processUploadedQrToSvg(effectiveQrImage, { colorDark: '#ED0080' })
      .then((res) => {
        if (!isMounted || !res) return;
        setDigitalSvgString(res.svgString);
      })
      .catch((err) => {
        console.warn('QR processing error:', err);
      })
      .finally(() => {
        if (isMounted) setIsProcessingSvg(false);
      });

    return () => { isMounted = false; };
  }, [effectiveQrImage]);

  // Periodic FPX status polling
  useEffect(() => {
    if (pendingFpxBill && step === 3) {
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);

      pollTimerRef.current = setInterval(async () => {
        try {
          const res = await verifyToyyibPayBillStatus(pendingFpxBill.billCode);
          if (res.status === 'Disahkan') {
            clearInterval(pollTimerRef.current);
            const confirmed = await confirmDonationSuccessDB(pendingFpxBill.donationId);
            if (confirmed.donation) {
              setCompletedDonation(confirmed.donation);
              setStep(4);
              setPendingFpxBill(null);
              showToast('success', `Alhamdulillah! Bayaran FPX ${formatCurrencyMYR(amount)} telah disahkan berjaya.`);
              loadCampaignData();
            }
          } else if (res.status === 'Gagal') {
            clearInterval(pollTimerRef.current);
            await markDonationFailedDB(pendingFpxBill.donationId);
            showToast('error', 'Transaksi FPX dibatalkan atau tidak berjaya oleh pihak bank.');
            setIsVerifyingFpx(false);
          }
        } catch { }
      }, 5000);


      return () => {
        if (pollTimerRef.current) clearInterval(pollTimerRef.current);
      };
    }
  }, [pendingFpxBill, step, amount]);

  if (!campaign && !isLoading) {
    return (
      <div className="p-12 text-center bg-white rounded-3xl border border-slate-200 space-y-4">
        <Heart className="w-10 h-10 text-slate-300 mx-auto" />
        <h3 className="font-extrabold text-base text-slate-900">Kempen Tidak Ditemui</h3>
        <button
          type="button"
          onClick={onBack}
          className="px-4 py-2 bg-asmm-red text-white text-xs font-bold rounded-xl cursor-pointer"
        >
          Kembali ke Senarai Tabung
        </button>
      </div>
    );
  }

  if (!campaign) return null;

  const percent = Math.min(100, Math.round((campaign.currentAmount / (campaign.targetAmount || 1)) * 100));

  const getDaysLeft = (): string => {
    if (!campaign.endDate) return 'Berterusan';
    const end = new Date(campaign.endDate).getTime();
    const now = new Date().getTime();
    const diff = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
    if (diff < 0) return 'Tamat';
    if (diff === 0) return 'Hari Terakhir';
    return `${diff} hari lagi`;
  };

  const handleSelectPreset = (val: number) => {
    setAmount(val);
    setCustomAmountStr(String(val));
  };

  const handleCustomAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.replace(/[^0-9]/g, '');
    setCustomAmountStr(val);
    const num = Number(val);
    if (num > 0) setAmount(num);
  };

  const handleCopyAccount = () => {
    navigator.clipboard.writeText(effectiveAccountNo.replace(/[^0-9]/g, ''));
    setCopiedAcc(true);
    showToast('success', `No. Akaun ${effectiveBankName} disalin`);
    setTimeout(() => setCopiedAcc(false), 3000);
  };

  const handleDownloadQr = () => {
    if (!digitalSvgString && !effectiveQrImage) return;
    const a = document.createElement('a');
    a.href = effectiveQrImage || `data:image/svg+xml;charset=utf-8,${encodeURIComponent(digitalSvgString)}`;
    a.download = `Kod_QR_DuitNow_${(campaign?.title || 'Infaq').replace(/\s+/g, '_')}.svg`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    showToast('success', 'Kod QR dimuat turun.');
  };

  const handleShare = () => {
    const baseTab = typeof window !== 'undefined' && window.location.pathname.startsWith('/donasi') ? 'donasi' : 'donations';
    const url = typeof window !== 'undefined' ? `${window.location.origin}/${baseTab}/${campaign.id}` : `https://asmm.pages.dev/donations/${campaign.id}`;
    const text = `Mari bersama berinfaq untuk *${campaign.title}* anjuran ASMM: ${url}`;
    navigator.clipboard.writeText(text);
    showToast('success', 'Pautan kempen disalin ke papan keratan');
  };

  const handleProcessPayment = async () => {
    if (amount <= 0) {
      showToast('warning', 'Sila masukkan jumlah sumbangan yang sah.');
      return;
    }

    const effectiveDonorName = isAnonymous ? 'Hamba Allah' : (donorName.trim() || currentUser?.name || 'Hamba Allah');
    const effectiveEmail = donorEmail.trim() || currentUser?.email || 'infaq@asmm.org.my';
    const effectivePhone = donorPhone.trim() || currentUser?.phone || '0194589201';
    const generatedId = `don-${Date.now()}-${Math.floor(Math.random() * 1000)}`;

    setIsProcessing(true);

    try {
      if (paymentMethod === 'ToyyibPay FPX') {
        const baseTab = typeof window !== 'undefined' && window.location.pathname.startsWith('/donasi') ? 'donasi' : 'donations';
        const returnBase = typeof window !== 'undefined' ? `${window.location.origin}/${baseTab}/${campaign.id}` : `https://asmm.pages.dev/donations/${campaign.id}`;
        const bill = await createToyyibPayBill({
          categoryCode: persatuanSettings.toyyibPayCategoryCode || 'htnqvub2',
          billName: `Infaq: ${campaign.title.slice(0, 20)}`,
          billDescription: `Sumbangan Infaq ASMM - ${campaign.title}`,
          billPriceSetting: 1,
          billPayorName: effectiveDonorName,
          billPayorEmail: effectiveEmail,
          billPayorPhone: effectivePhone,
          billAmount: amount,
          billReturnUrl: returnBase
        });

        if (!bill || !bill.billCode) {
          throw new Error("Gagal menjana pautan bayaran daripada ToyyibPay.");
        }

        const activeBillCode = bill.billCode;
        const activePaymentUrl = bill.paymentUrl;

        const pendingDonation: DonationRecord = {
          id: generatedId,
          campaignId: campaign.id,
          campaignTitle: campaign.title,
          amount,
          donorName: effectiveDonorName,
          isAnonymous,
          donorEmail: effectiveEmail,
          donorPhone: effectivePhone,
          message: message.trim() || undefined,
          infaqType: campaign.category as any,
          paymentMethod: 'ToyyibPay FPX',
          transactionRef: activeBillCode,
          status: 'Menunggu Pengesahan',
          createdAt: new Date().toLocaleString('ms-MY', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
          }).replace(',', '')
        };

        await recordDonationDB(pendingDonation);

        setPendingFpxBill({
          billCode: activeBillCode,
          paymentUrl: activePaymentUrl,
          donationId: generatedId
        });
        setStep(3);
        setIsProcessing(false);
        window.open(activePaymentUrl, '_blank');
        showToast('info', 'Sila lengkapkan bayaran di tetingkap perbankan ToyyibPay FPX.');
      } else {

        const txRef = paymentMethod === 'DuitNow QR'
          ? `QR-DN-${Date.now().toString().slice(-6)}`
          : transferRef.trim() || `MANUAL-${Date.now().toString().slice(-6)}`;

        const newDonation: DonationRecord = {
          id: generatedId,
          campaignId: campaign.id,
          campaignTitle: campaign.title,
          amount,
          donorName: effectiveDonorName,
          isAnonymous,
          donorEmail: effectiveEmail,
          donorPhone: effectivePhone,
          message: message.trim() || undefined,
          infaqType: campaign.category as any,
          paymentMethod,
          transactionRef: txRef,
          status: 'Berjaya',
          createdAt: new Date().toLocaleString('ms-MY', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
          }).replace(',', '')
        };

        await recordDonationDB(newDonation);
        setCompletedDonation(newDonation);
        setStep(4);
        setIsProcessing(false);
        showToast('success', `Alhamdulillah! Infaq ${formatCurrencyMYR(amount)} direkodkan.`);
        loadCampaignData();
      }
    } catch (err: any) {
      setIsProcessing(false);
      showToast('error', err?.message || 'Ralat semasa memproses sumbangan.');
    }

  };

  const handleManualCheckFpxStatus = async () => {
    if (!pendingFpxBill) return;
    setIsVerifyingFpx(true);

    try {
      const res = await verifyToyyibPayBillStatus(pendingFpxBill.billCode);

      if (res.status === 'Disahkan') {
        const confirmed = await confirmDonationSuccessDB(pendingFpxBill.donationId);
        if (confirmed.donation) {
          setCompletedDonation(confirmed.donation);
          setStep(4);
          setPendingFpxBill(null);
          showToast('success', 'Alhamdulillah! Bayaran FPX telah disahkan berjaya.');
          loadCampaignData();
        }
      } else if (res.status === 'Gagal') {
        await markDonationFailedDB(pendingFpxBill.donationId);
        showToast('error', 'Transaksi FPX dibatalkan atau tidak berjaya oleh pihak bank.');
      } else if (res.status === 'Menunggu Pengesahan') {
        showToast('warning', 'Bayaran masih belum selesai di portal bank. Sila pastikan anda selesai membuat pembayaran.');
      } else {
        showToast('error', 'Transaksi FPX tidak berjaya atau telah dibatalkan.');
      }

    } catch {
      showToast('error', 'Gagal menyemak status FPX.');
    } finally {
      setIsVerifyingFpx(false);
    }
  };

  return (
    <div className="w-full space-y-6 min-h-[85vh]">
      {/* Top Header */}
      <PageHeader
        badgeText="Tabung"
        badgeIcon={ShieldCheck}
        title="Butiran Kempen"
      >
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onBack}
            className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs rounded-xl flex items-center gap-1.5 transition-all cursor-pointer"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            <span>Semua Tabung</span>
          </button>

          {isManagement && onEdit && (
            <button
              type="button"
              onClick={() => onEdit(campaign)}
              className="px-3.5 py-1.5 bg-slate-50 hover:bg-slate-100 text-slate-800 font-bold text-xs rounded-xl border border-slate-200 flex items-center gap-1.5 cursor-pointer"
            >
              <Edit3 className="w-3.5 h-3.5" />
              <span>Urus Kempen</span>
            </button>
          )}

          <button
            type="button"
            onClick={handleShare}
            className="px-3 py-1.5 bg-slate-50 hover:bg-slate-100 text-slate-800 font-bold text-xs rounded-xl border border-slate-200 flex items-center gap-1.5 cursor-pointer"
          >
            <Share2 className="w-3.5 h-3.5" />
            <span>Kongsi</span>
          </button>
        </div>
      </PageHeader>

      {/* Main Two-Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        {/* Left Column (Campaign Narrative, Progress, Sokongan & Doa) */}
        <div className="lg:col-span-7 space-y-6">
          {/* Main Campaign Showcase Card */}
          <div className="bg-white rounded-3xl p-6 sm:p-7 border border-slate-200 shadow-sm space-y-5">
            <div className="space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                {campaign.isFeatured && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-0.5 bg-red-50 text-asmm-red text-[11px] font-extrabold rounded-md border border-red-100">
                    <Sparkles className="w-3 h-3" />
                    Pilihan Utama
                  </span>
                )}
                <span className={`px-2.5 py-0.5 text-[11px] font-bold rounded-md border ${getCategoryColor(campaign.category)}`}>
                  {campaign.category}
                </span>
                <span className="text-xs text-slate-500 font-medium">
                  • {campaign.beneficiaryGelanggang === 'Semua Gelanggang' ? 'Semua Gelanggang' : (campaign.beneficiaryGelanggang ? `Gelanggang ${campaign.beneficiaryGelanggang}` : 'Semua Gelanggang')}
                </span>
              </div>

              <h2 className="text-xl sm:text-2xl font-black text-slate-900 tracking-tight leading-snug">
                {campaign.title}
              </h2>
            </div>

            {/* Visual Progress Bar */}
            <div className="bg-slate-50 p-4 sm:p-5 rounded-2xl border border-slate-200 space-y-2.5">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <div>
                  <span className="text-2xl sm:text-3xl font-black text-slate-900">
                    {formatCurrencyMYR(campaign.currentAmount)}
                  </span>
                  <span className="text-xs text-slate-500 ml-1.5">
                    dari sasaran <strong className="text-slate-700">{formatCurrencyMYR(campaign.targetAmount)}</strong>
                  </span>
                </div>

                <div className="flex items-center gap-3 text-xs font-bold text-slate-600">
                  <span className="flex items-center gap-1">
                    <Users className="w-3.5 h-3.5 text-slate-500" />
                    <span>{campaign.donorCount} penderma</span>
                  </span>
                  <span className="flex items-center gap-1">
                    <Calendar className="w-3.5 h-3.5 text-slate-500" />
                    <span>{getDaysLeft()}</span>
                  </span>
                  <span className="text-xs font-extrabold text-asmm-red bg-red-50 border border-red-100 px-2 py-0.5 rounded-md">
                    {percent}%
                  </span>
                </div>
              </div>

              <div className="w-full bg-slate-200 h-2.5 rounded-full overflow-hidden">
                <div
                  className="bg-asmm-red h-full rounded-full transition-all duration-500"
                  style={{ width: `${percent}%` }}
                />
              </div>
            </div>

            {/* Campaign Narrative Story */}
            <div className="space-y-3 pt-2">
              <h3 className="font-extrabold text-sm text-slate-900">Mengenai Inisiatif Ini</h3>
              <p className="text-xs sm:text-sm text-slate-700 leading-relaxed whitespace-pre-line">
                {campaign.story}
              </p>
            </div>

            {/* Impact Milestones */}
            {campaign.impactMilestones && campaign.impactMilestones.length > 0 && (
              <div className="space-y-2.5 pt-2 border-t border-slate-100">
                <h3 className="font-extrabold text-xs text-slate-900 uppercase tracking-wider">
                  Pecahan Penggunaan Dana
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {campaign.impactMilestones.map((m, idx) => (
                    <div
                      key={idx}
                      className="p-3 bg-slate-50 rounded-xl border border-slate-200 text-xs space-y-0.5"
                    >
                      <div className="font-black text-asmm-red">{formatCurrencyMYR(m.amount)}</div>
                      <div className="text-slate-600 font-medium">{m.description}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* SOKONGAN & DOA SECTION (Inside Single Campaign Detail) */}
          <div className="bg-white rounded-3xl p-6 sm:p-7 border border-slate-200 shadow-sm space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-extrabold text-base text-slate-900 flex items-center gap-2">
                  <Heart className="w-4 h-4 text-asmm-red fill-asmm-red" />
                  <span>Sokongan & Doa Penderma</span>
                </h3>
                <p className="text-xs text-slate-500">
                  {donations.length} sumbangan direkodkan untuk tabung ini
                </p>
              </div>

              <span className="text-[10px] bg-emerald-50 text-emerald-700 font-bold px-2.5 py-1 rounded-full border border-emerald-200 flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-ping" />
                <span>Terkini</span>
              </span>
            </div>

            {donations.length === 0 ? (
              <div className="p-8 bg-slate-50 rounded-2xl border border-slate-200 text-center space-y-2">
                <Heart className="w-6 h-6 text-slate-300 mx-auto" />
                <div className="text-xs font-bold text-slate-700">Jadilah Penderma Terawal!</div>
                <p className="text-[11px] text-slate-500">
                  Belum ada rekod sumbangan bagi tabung ini. Ayuh jadi insan pertama yang menyumbang.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {donations.map((d) => {
                  const displayName = d.isAnonymous ? 'Hamba Allah' : d.donorName;
                  const initial = d.isAnonymous ? 'HA' : displayName.charAt(0).toUpperCase();

                  return (
                    <div
                      key={d.id}
                      className="p-4 bg-slate-50 rounded-2xl border border-slate-200 space-y-2"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2.5">
                          <div className="w-8 h-8 rounded-xl bg-white border border-slate-200 text-asmm-red font-black text-xs flex items-center justify-center shadow-2xs shrink-0">
                            {initial}
                          </div>
                          <div>
                            <div className="text-xs font-extrabold text-slate-900 flex items-center gap-1">
                              <span>{displayName}</span>
                              {!d.isAnonymous && <UserCheck className="w-3 h-3 text-emerald-600 shrink-0" />}
                            </div>
                            <div className="text-[10px] text-slate-400">
                              {d.createdAt}
                            </div>
                          </div>
                        </div>

                        <div className="text-xs font-black text-asmm-red">
                          {formatCurrencyMYR(d.amount)}
                        </div>
                      </div>

                      {d.message && (
                        <div className="text-xs text-slate-700 bg-white p-2.5 rounded-xl border border-slate-200 leading-relaxed italic flex items-start gap-2">
                          <MessageSquare className="w-3.5 h-3.5 text-slate-400 shrink-0 mt-0.5" />
                          <span>"{d.message}"</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Right Column (In-Page Donation Checkout Box — Zero Modal) */}
        <div className="lg:col-span-5 space-y-4 lg:sticky lg:top-20">
          <div className="bg-white rounded-3xl p-6 sm:p-7 border border-slate-200 shadow-md space-y-5">
            {/* Header */}
            <div className="flex items-center justify-between pb-3 border-b border-slate-100">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-xl bg-red-50 text-asmm-red flex items-center justify-center border border-red-100">
                  <Heart className="w-4 h-4 fill-asmm-red" />
                </div>
                <div>
                  <h3 className="text-sm font-extrabold text-slate-900">Borang Sumbangan Infaq</h3>
                  <div className="text-[11px] text-slate-500">Pindahan Pantas & Selamat</div>
                </div>
              </div>

              {step < 4 && (
                <div className="text-xs font-bold text-asmm-red bg-red-50 px-2 py-0.5 rounded-md border border-red-100">
                  Langkah {step}/3
                </div>
              )}
            </div>

            {/* Step 1: Amount */}
            {step === 1 && (
              <div className="space-y-4 text-xs">
                <div className="font-extrabold text-sm text-slate-900">Pilih Jumlah Sumbangan</div>

                <div className="grid grid-cols-3 gap-2">
                  {PRESET_AMOUNTS.map((val) => (
                    <button
                      key={val}
                      type="button"
                      onClick={() => handleSelectPreset(val)}
                      className={`py-3 px-2 rounded-xl font-black text-xs transition-all border text-center cursor-pointer ${amount === val
                        ? 'bg-asmm-red text-white border-asmm-red shadow-xs'
                        : 'bg-white hover:bg-slate-50 text-slate-800 border-slate-200'
                        }`}
                    >
                      {formatCurrencyMYR(val)}
                    </button>
                  ))}
                </div>

                <div className="space-y-1">
                  <label className="font-bold text-slate-700 block">Jumlah Pilihan (RM):</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 font-bold text-slate-400">RM</span>
                    <input
                      type="text"
                      value={customAmountStr}
                      onChange={handleCustomAmountChange}
                      placeholder="Contoh: 150"
                      className="w-full pl-10 pr-3 py-2 bg-white border border-slate-200 focus:border-asmm-red rounded-xl font-bold text-slate-900 outline-hidden"
                    />
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => {
                    if (amount <= 0) {
                      showToast('warning', 'Sila masukkan jumlah sumbangan.');
                      return;
                    }
                    setStep(2);
                  }}
                  className="w-full py-3 bg-asmm-red hover:bg-asmm-crimson text-white font-extrabold text-xs rounded-xl shadow-xs flex items-center justify-center gap-1.5 cursor-pointer"
                >
                  <span>Seterusnya: Maklumat Penderma</span>
                  <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </div>
            )}

            {/* Step 2: Donor Info */}
            {step === 2 && (
              <div className="space-y-4 text-xs">
                <div className="font-extrabold text-sm text-slate-900">Maklumat Penderma</div>

                <label className="p-3 bg-slate-50 rounded-xl border border-slate-200 flex items-center justify-between cursor-pointer">
                  <div className="flex items-center gap-2">
                    <EyeOff className="w-4 h-4 text-slate-600" />
                    <span className="font-bold text-slate-800">Sumbang sebagai "Hamba Allah" (Rahsia)</span>
                  </div>
                  <input
                    type="checkbox"
                    checked={isAnonymous}
                    onChange={(e) => setIsAnonymous(e.target.checked)}
                    className="w-4 h-4 text-asmm-red rounded border-slate-300"
                  />
                </label>

                {!isAnonymous && (
                  <div className="space-y-1">
                    <label className="font-bold text-slate-700 block">Nama Penuh:</label>
                    <input
                      type="text"
                      value={donorName}
                      onChange={(e) => setDonorName(e.target.value)}
                      placeholder="Contoh: Hj. Ahmad"
                      className="w-full px-3 py-2 bg-white border border-slate-200 focus:border-asmm-red rounded-xl font-medium outline-hidden"
                    />
                  </div>
                )}

                <div className="space-y-1">
                  <label className="font-bold text-slate-700 block">Emel (Untuk Resit):</label>
                  <input
                    type="email"
                    value={donorEmail}
                    onChange={(e) => setDonorEmail(e.target.value)}
                    placeholder="nama@email.com"
                    className="w-full px-3 py-2 bg-white border border-slate-200 focus:border-asmm-red rounded-xl font-medium outline-hidden"
                  />
                </div>

                <div className="space-y-1">
                  <label className="font-bold text-slate-700 block">No. Telefon:</label>
                  <input
                    type="tel"
                    value={donorPhone}
                    onChange={(e) => setDonorPhone(e.target.value)}
                    placeholder="0194589201"
                    className="w-full px-3 py-2 bg-white border border-slate-200 focus:border-asmm-red rounded-xl font-medium outline-hidden"
                  />
                </div>

                <div className="space-y-1">
                  <label className="font-bold text-slate-700 block">Doa / Kata Semangat (Pilihan):</label>
                  <textarea
                    rows={2}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Semoga persatuan terus cemerlang..."
                    className="w-full px-3 py-2 bg-white border border-slate-200 focus:border-asmm-red rounded-xl outline-hidden resize-none"
                  />
                </div>

                <div className="flex items-center gap-2 pt-1">
                  <button
                    type="button"
                    onClick={() => setStep(1)}
                    className="px-3.5 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl flex items-center gap-1 cursor-pointer"
                  >
                    <ChevronLeft className="w-3.5 h-3.5" />
                    <span>Kembali</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => setStep(3)}
                    className="flex-1 py-2.5 bg-asmm-red hover:bg-asmm-crimson text-white font-extrabold rounded-xl shadow-xs flex items-center justify-center gap-1 cursor-pointer"
                  >
                    <span>Seterusnya: Bayaran</span>
                    <ChevronRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            )}

            {/* Step 3: Payment */}
            {step === 3 && (
              <div className="space-y-4 text-xs">
                {!pendingFpxBill ? (
                  <>
                    <div className="flex items-center justify-between">
                      <span className="font-extrabold text-sm text-slate-900">Pilih Kaedah Bayaran</span>
                      <span className="font-black text-asmm-red text-sm">{formatCurrencyMYR(amount)}</span>
                    </div>

                    <div className={`grid gap-2 ${persatuanSettings.fpxEnabled ? 'grid-cols-3' : 'grid-cols-2'}`}>
                      {persatuanSettings.fpxEnabled && (
                        <button
                          type="button"
                          onClick={() => setPaymentMethod('ToyyibPay FPX')}
                          className={`p-2.5 rounded-xl border text-center transition-all cursor-pointer font-bold ${paymentMethod === 'ToyyibPay FPX'
                            ? 'bg-red-50 text-asmm-red border-asmm-red'
                            : 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100'
                            }`}
                        >
                          <CreditCard className="w-4 h-4 mx-auto mb-1" />
                          <span>FPX Online</span>
                        </button>
                      )}

                      <button
                        type="button"
                        onClick={() => setPaymentMethod('DuitNow QR')}
                        className={`p-2.5 rounded-xl border text-center transition-all cursor-pointer font-bold ${paymentMethod === 'DuitNow QR'
                          ? 'bg-red-50 text-asmm-red border-asmm-red'
                          : 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100'
                          }`}
                      >
                        <QrCode className="w-4 h-4 mx-auto mb-1" />
                        <span>DuitNow QR</span>
                      </button>

                      <button
                        type="button"
                        onClick={() => setPaymentMethod('Bank Transfer')}
                        className={`p-2.5 rounded-xl border text-center transition-all cursor-pointer font-bold ${paymentMethod === 'Bank Transfer'
                          ? 'bg-red-50 text-asmm-red border-asmm-red'
                          : 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100'
                          }`}
                      >
                        <Upload className="w-4 h-4 mx-auto mb-1" />
                        <span>Pindahan</span>
                      </button>
                    </div>

                    {paymentMethod === 'ToyyibPay FPX' && (
                      <div className="p-3 bg-slate-50 border border-slate-200 rounded-xl space-y-1">
                        <div className="font-bold text-slate-900">Perbankan FPX (ToyyibPay)</div>
                        <p className="text-[11px] text-slate-600">
                          Maybank2u, CIMB Clicks, Bank Islam, RHB, BSN, dan semua bank Malaysia.
                        </p>
                      </div>
                    )}

                    {paymentMethod === 'DuitNow QR' && (
                      <div className="p-4 bg-slate-50 border border-slate-200 rounded-2xl space-y-3 text-center">
                        <div className="flex items-center justify-between text-[11px]">
                          <span className="font-bold text-slate-700">Imbas Kod DuitNow QR</span>
                          {digitalSvgString ? (
                            <span className="text-[10px] font-black uppercase tracking-wider text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200">
                              Digital SVG 1:1
                            </span>
                          ) : null}
                        </div>

                        {/* 1:1 Vector SVG Display Container */}
                        <div className="w-48 h-48 sm:w-56 sm:h-56 mx-auto bg-white p-3.5 rounded-3xl border-2 border-slate-900/10 shadow-sm relative flex items-center justify-center">
                          {isProcessingSvg && (
                            <div className="absolute inset-0 bg-white/95 backdrop-blur-xs flex flex-col items-center justify-center rounded-2xl z-10 space-y-2 text-center p-3">
                              <Loader2 className="w-7 h-7 text-amber-500 animate-spin" />
                              <span className="text-xs font-black text-slate-800">Menjana Digital SVG 1:1...</span>
                              <span className="text-[10px] text-slate-500">Mengekstrak kod DuitNow QR</span>
                            </div>
                          )}

                          {digitalSvgString ? (
                            <div
                              className="w-full h-full aspect-square flex items-center justify-center [&>svg]:w-full [&>svg]:h-full [&>svg]:aspect-square [&>svg]:block"
                              dangerouslySetInnerHTML={{ __html: digitalSvgString }}
                            />
                          ) : (
                            <img
                              src={effectiveQrImage || '/badges/qr-persatuan-asmm.png'}
                              alt="DuitNow QR Infaq"
                              className="w-full h-full aspect-square object-contain mx-auto rounded-xl"
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                if (target.src.indexOf('/badges/qr-persatuan-asmm.png') === -1) {
                                  target.src = '/badges/qr-persatuan-asmm.png';
                                }
                              }}
                            />
                          )}
                        </div>

                        {/* Bank and Account Details */}
                        <div className="bg-white p-3 rounded-xl border border-slate-200 space-y-1 text-left">
                          <div className="flex items-center justify-between">
                            <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Akaun Rasmi:</span>
                            <span className="text-[10px] font-extrabold text-asmm-red">{effectiveBankName}</span>
                          </div>
                          <div className="flex items-center justify-between gap-2">
                            <span className="font-mono font-black text-sm text-slate-900">{effectiveAccountNo}</span>
                            <button
                              type="button"
                              onClick={handleCopyAccount}
                              className="px-2 py-1 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-[10px] rounded-lg border border-slate-200 flex items-center gap-1 cursor-pointer shrink-0"
                            >
                              {copiedAcc ? <Check className="w-3 h-3 text-emerald-600" /> : <Copy className="w-3 h-3" />}
                              <span>{copiedAcc ? 'Disalin' : 'Salin No.'}</span>
                            </button>
                          </div>
                          <p className="text-[10px] text-slate-600 font-medium truncate">{effectiveAccountHolder}</p>
                        </div>

                        {/* Download SVG button */}
                        {(digitalSvgString || effectiveQrImage) && (
                          <div className="pt-1 flex justify-center">
                            <button
                              type="button"
                              onClick={handleDownloadQr}
                              className="inline-flex items-center gap-1.5 px-4 py-1.5 bg-slate-900 hover:bg-slate-800 text-white text-[11px] font-bold rounded-xl transition-all shadow-xs cursor-pointer"
                            >
                              <Download className="w-3.5 h-3.5 text-amber-400" />
                              <span>Muat Turun Kod QR</span>
                            </button>
                          </div>
                        )}

                        {persatuanSettings.paymentInstructions && (
                          <p className="text-[10px] text-slate-500 font-medium italic">
                            "{persatuanSettings.paymentInstructions}"
                          </p>
                        )}
                      </div>
                    )}

                    {paymentMethod === 'Bank Transfer' && (
                      <div className="p-4 bg-slate-50 border border-slate-200 rounded-2xl space-y-3">
                        <div className="bg-white p-3 rounded-xl border border-slate-200 space-y-1 text-left">
                          <div className="flex items-center justify-between">
                            <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Pindahan Bank Manual:</span>
                            <span className="text-[10px] font-extrabold text-asmm-red">{effectiveBankName}</span>
                          </div>
                          <div className="flex items-center justify-between gap-2">
                            <span className="font-mono font-black text-sm text-slate-900">{effectiveAccountNo}</span>
                            <button
                              type="button"
                              onClick={handleCopyAccount}
                              className="px-2 py-1 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-[10px] rounded-lg border border-slate-200 flex items-center gap-1 cursor-pointer shrink-0"
                            >
                              {copiedAcc ? <Check className="w-3 h-3 text-emerald-600" /> : <Copy className="w-3 h-3" />}
                              <span>{copiedAcc ? 'Disalin' : 'Salin No.'}</span>
                            </button>
                          </div>
                          <p className="text-[10px] text-slate-600 font-medium">{effectiveAccountHolder}</p>
                        </div>

                        {persatuanSettings.paymentInstructions && (
                          <p className="text-[10px] text-slate-500 font-medium italic">
                            "{persatuanSettings.paymentInstructions}"
                          </p>
                        )}

                        <div className="space-y-1">
                          <label className="text-[11px] font-bold text-slate-700 block">No. Rujukan Pindahan (Pilihan):</label>
                          <input
                            type="text"
                            value={transferRef}
                            onChange={(e) => setTransferRef(e.target.value)}
                            placeholder="Contoh: REF98765432"
                            className="w-full px-3 py-2 bg-white border border-slate-200 focus:border-asmm-red rounded-xl text-xs outline-hidden"
                          />
                        </div>
                      </div>
                    )}

                    <div className="flex items-center gap-2 pt-1">
                      <button
                        type="button"
                        onClick={() => setStep(2)}
                        className="px-3.5 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl flex items-center gap-1 cursor-pointer"
                      >
                        <ChevronLeft className="w-3.5 h-3.5" />
                        <span>Kembali</span>
                      </button>

                      <button
                        type="button"
                        disabled={isProcessing}
                        onClick={handleProcessPayment}
                        className="flex-1 py-2.5 bg-asmm-red hover:bg-asmm-crimson text-white font-black rounded-xl shadow-xs flex items-center justify-center gap-1.5 cursor-pointer disabled:opacity-50"
                      >
                        <Heart className="w-3.5 h-3.5 fill-white" />
                        <span>{isProcessing ? 'Menjana Bil FPX...' : `Bayar Infaq ${formatCurrencyMYR(amount)}`}</span>
                      </button>
                    </div>
                  </>
                ) : (
                  /* FPX Awaiting Verification Screen */
                  <div className="space-y-4 text-center py-2">
                    <div className="w-12 h-12 bg-amber-50 text-amber-600 rounded-full flex items-center justify-center mx-auto border border-amber-200">
                      <Clock className="w-6 h-6 animate-pulse" />
                    </div>

                    <div className="space-y-1">
                      <h4 className="text-base font-extrabold text-slate-900">Menunggu Pembayaran FPX</h4>
                      <p className="text-xs text-slate-600">
                        Sila lengkapkan bayaran <strong>{formatCurrencyMYR(amount)}</strong> pada portal bank yang dibuka.
                      </p>
                    </div>

                    <div className="p-3 bg-slate-50 rounded-xl border border-slate-200 text-left space-y-1">
                      <div className="flex justify-between">
                        <span className="text-slate-500">Bill Code:</span>
                        <span className="font-mono font-bold text-slate-800">{pendingFpxBill.billCode}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Status:</span>
                        <span className="text-amber-600 font-bold flex items-center gap-1">
                          <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-ping"></span>
                          Menunggu Pengesahan Bank
                        </span>
                      </div>
                    </div>

                    <div className="space-y-2 pt-1">
                      <button
                        type="button"
                        onClick={() => window.open(pendingFpxBill.paymentUrl, '_blank')}
                        className="w-full py-2.5 px-3 bg-slate-100 hover:bg-slate-200 text-slate-800 font-bold text-xs rounded-xl flex items-center justify-center gap-1.5 cursor-pointer border border-slate-200"
                      >
                        <ExternalLink className="w-3.5 h-3.5 text-slate-600" />
                        <span>Buka Semula Laman Bank (ToyyibPay)</span>
                      </button>

                      <button
                        type="button"
                        disabled={isVerifyingFpx}
                        onClick={handleManualCheckFpxStatus}
                        className="w-full py-2.5 px-3 bg-asmm-red hover:bg-asmm-crimson text-white font-extrabold text-xs rounded-xl shadow-xs flex items-center justify-center gap-1.5 cursor-pointer disabled:opacity-50"
                      >
                        <RefreshCw className={`w-3.5 h-3.5 ${isVerifyingFpx ? 'animate-spin' : ''}`} />
                        <span>{isVerifyingFpx ? 'Menyemak Status FPX...' : 'Semak Status Bayaran Sekarang'}</span>
                      </button>

                      <button
                        type="button"
                        onClick={() => setPendingFpxBill(null)}
                        className="text-[11px] text-slate-500 hover:text-slate-800 font-bold underline pt-1 cursor-pointer"
                      >
                        Tukar Kaedah Bayaran
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Step 4: Success & Confirmation */}
            {step === 4 && completedDonation && (
              <div className="space-y-4 text-center py-2 text-xs">
                <div className="w-12 h-12 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto">
                  <CheckCircle2 className="w-7 h-7" />
                </div>

                <div>
                  <h4 className="text-base font-extrabold text-slate-900">Sumbangan Diterima</h4>
                  <p className="text-xs text-slate-600 mt-0.5">
                    Terima kasih kepada <strong>{completedDonation.isAnonymous ? 'Hamba Allah' : completedDonation.donorName}</strong> ({formatCurrencyMYR(completedDonation.amount)}).
                  </p>
                </div>

                <div className="p-3 bg-slate-50 rounded-xl border border-slate-200 text-left space-y-1">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Rujukan:</span>
                    <span className="font-mono font-bold text-slate-800">{completedDonation.transactionRef}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Kempen:</span>
                    <span className="font-bold text-slate-800 truncate max-w-xs">{completedDonation.campaignTitle}</span>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => setIsReceiptOpen(true)}
                  className="w-full py-2.5 px-3 bg-slate-100 hover:bg-slate-200 text-slate-900 font-bold text-xs rounded-xl flex items-center justify-center gap-1.5 cursor-pointer"
                >
                  <FileText className="w-3.5 h-3.5 text-asmm-red" />
                  <span>Lihat & Cetak Resit Rasmi</span>
                </button>

                <div className="flex items-center justify-center gap-2 pt-1">
                  <button
                    type="button"
                    onClick={() => {
                      const url = window.location.href;
                      const text = `Saya baru sahaja menyumbang ke tabung *${campaign.title}* ASMM. Ayuh bersama menyokong: ${url}`;
                      window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(text)}`, '_blank');
                    }}
                    className="px-3 py-1.5 bg-emerald-600 text-white text-xs font-bold rounded-lg flex items-center gap-1 cursor-pointer"
                  >
                    <Share2 className="w-3 h-3" />
                    <span>Kongsi WhatsApp</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setStep(1);
                      setCompletedDonation(null);
                    }}
                    className="px-3 py-1.5 bg-slate-100 text-slate-700 text-xs font-bold rounded-lg cursor-pointer"
                  >
                    Sumbang Lagi
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Official ROS Infaq Receipt Modal (Printable Sheet) */}
      <OfficialDonationReceiptModal
        isOpen={isReceiptOpen}
        onClose={() => setIsReceiptOpen(false)}
        donation={completedDonation}
      />
    </div>
  );
};
